import { useEffect, useState } from 'react'

/**
 * How many pixels of the layout viewport the on-screen keyboard is covering.
 *
 * **iOS does not resize the page when the keyboard opens.** On Android the
 * layout viewport shrinks and a bottom-anchored element moves up on its own; on
 * iOS `window.innerHeight` is unchanged and the keyboard is simply drawn over
 * the page, so anything anchored to the bottom — a composer, a Save button —
 * ends up underneath it with no indication anything is wrong.
 *
 * The visual viewport is the part actually visible. The difference between it
 * and the layout viewport is the keyboard (plus any browser chrome that
 * overlaps), which is the number a composer needs to lift itself by.
 *
 * `offsetTop` matters because iOS scrolls the visual viewport within the layout
 * viewport when focusing an input near the bottom; without it the inset is
 * overstated by however far it scrolled and the composer jumps too high.
 *
 * Returns 0 where `visualViewport` is unsupported, which is the correct
 * fallback: those browsers resize the layout viewport instead, so no lift is
 * needed. **Not observable in an emulator** — Playwright has no keyboard.
 */
const useKeyboardInset = (): number => {
	const [inset, setInset] = useState(0)

	useEffect(() => {
		const vv = typeof window === 'undefined' ? undefined : window.visualViewport
		if (!vv) return

		const measure = () => {
			const covered = window.innerHeight - (vv.height + vv.offsetTop)
			// Small negatives and a pixel or two of rounding are normal with no
			// keyboard open; treating those as an inset makes the layout twitch.
			setInset(covered > 1 ? Math.round(covered) : 0)
		}

		measure()
		vv.addEventListener('resize', measure)
		vv.addEventListener('scroll', measure)
		return () => {
			vv.removeEventListener('resize', measure)
			vv.removeEventListener('scroll', measure)
		}
	}, [])

	return inset
}

export default useKeyboardInset
